import { z } from 'zod';
import { IdSchema, TimestampSchema } from './common.js';

/**
 * Microsoft Teams calling through ArcNet, Direct Routing (docs/44; owner, 19 October 2026).
 *
 * An account's connection is its subdomain under the platform's base domain, which its Microsoft 365
 * Global Administrator adds to their tenant; a seat rings in Teams when it has a Teams number.
 */

/** Where an account's setup has got, in the order the steps are done (docs/44 §2.1). */
export const TEAMS_STEPS = ['subdomain', 'verify', 'activate', 'route', 'users'] as const;
export type TeamsStep = (typeof TEAMS_STEPS)[number];

export const TeamsConnectionSchema = z.object({
  /** False when the platform has no base domain: Teams is not offered here. */
  available: z.boolean(),
  /** Null until the account starts. */
  connection: z.object({
    id: IdSchema,
    /** The subdomain the account adds to Microsoft 365: meridian.teams.arcnet.co.za. */
    fqdn: z.string(),
    label: z.string(),
    verify_txt: z.string().nullable(),
    txt_published_at: TimestampSchema.nullable(),
    verified_at: TimestampSchema.nullable(),
    status: z.enum(['setup', 'active', 'off']),
    last_call_in_at: TimestampSchema.nullable(),
    last_call_out_at: TimestampSchema.nullable(),
    created_at: TimestampSchema,
    /** The account's Microsoft 365 (Entra) tenant, once its admin has connected it (0168). */
    ms_tenant_id: z.string().nullable(),
    ms_connected_at: TimestampSchema.nullable(),
  }).nullable(),
  /** The PowerShell the account's admin runs: voice route, policy and numbers, with its values. */
  script: z.string().nullable(),
  seats: z.array(z.object({
    extension_id: IdSchema,
    number: z.string(),
    display_name: z.string().nullable(),
    teams_number: z.string(),
    /** The person's Microsoft 365 sign-in, whose Teams rings; null until it is given (0165). */
    teams_user: z.string().nullable(),
    enabled: z.boolean(),
  })),
});
export type TeamsConnection = z.infer<typeof TeamsConnectionSchema>;

/** Starting: the label the subdomain uses. */
export const TeamsStartSchema = z.object({
  label: z.string().trim().toLowerCase().regex(/^[a-z0-9]([a-z0-9-]{0,38}[a-z0-9])?$/, 'Letters, digits and hyphens, 2 to 40'),
});

/** The TXT value Microsoft gave the admin, and whether they have verified and activated it. */
export const TeamsUpdateSchema = z.object({
  verify_txt: z.string().trim().regex(/^MS=ms[0-9]{6,12}$/, 'It looks like MS=ms12345678').nullable().optional(),
  verified: z.boolean().optional(),
  status: z.enum(['setup', 'active', 'off']).optional(),
});
export type TeamsUpdate = z.infer<typeof TeamsUpdateSchema>;

/** A seat's Teams number: E.164 with +, or a shared base number and the seat's extension. */
export const TeamsSeatSchema = z.object({
  teams_number: z.string().trim().regex(/^\+[1-9][0-9]{6,14}(;ext=[0-9]{1,8})?$/,
    'International format, like +27215550100, or a shared number and extension, like +27215550100;ext=1001'),
  /** Their Microsoft 365 sign-in (UPN), e.g. tim@contoso.com. Empty clears it. */
  teams_user: z.preprocess((v) => (typeof v === 'string' && v.trim() === '' ? null : v),
    z.string().trim().max(254).regex(/^[^@\s"`$]+@[A-Za-z0-9.-]+\.[A-Za-z]{2,}$/, 'Their Microsoft 365 sign-in, like tim@contoso.com')
      .nullable().optional()),
  enabled: z.boolean().default(true),
});
export type TeamsSeat = z.infer<typeof TeamsSeatSchema>;

/** Each Teams seat's live state (GET /tenants/:t/teams/status): its Teams presence and the checks. */
export const TeamsStatusSchema = z.object({
  /** Microsoft answering our trunk within its 3-minute rule; null when the edge's record is not readable. */
  trunk_up: z.boolean().nullable(),
  microsoft_connected: z.boolean(),
  seats: z.array(z.object({
    extension_id: IdSchema,
    presence: z.object({ availability: z.string(), activity: z.string(), at: TimestampSchema }).nullable(),
    checks: z.object({
      enabled: z.boolean(),
      trunk_up: z.boolean().nullable(),
      sign_in_set: z.boolean(),
      user_found: z.boolean().nullable(),
      signed_in: z.boolean().nullable(),
    }),
    can_receive: z.boolean(),
  })),
});
export type TeamsStatus = z.infer<typeof TeamsStatusSchema>;
